// Composer — message input with @mention autocomplete. Typing "@name" opens a
// directory dropdown (plus @Dana); picking inserts "@Full Name" and records the
// user_id, which is sent as mention_user_ids. Mentioning @Dana triggers Dana's
// async reply server-side. Enter sends; Shift+Enter newlines.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trophy } from 'lucide-react';
import { DANA_BOT_USER_ID } from '../types';
import type { MessageGif, NewPoll } from '../types';
import type { Directory, Person } from '../hooks/useDirectory';
import { conversationsApi } from '../api/conversationsApi';
import type { RecognitionContextMessage } from '@/services/recognitionService';
import { useRecognitionDraft } from '../useRecognitionDraft';
import { CelebrationBurst, celebrationMessage } from './CelebrationBurst';
import { GiveRecognitionComposer, type PostedRecognition } from '@/features/performance/GiveRecognitionComposer';
import type { EmployeeHit } from '@/services/workforceService';
import { VoiceInputButton } from '../../shared/components/VoiceInputButton';
import { PlusMenu } from './PlusMenu';
import { DanaAssist } from './DanaAssist';
import { GifPicker } from './GifPicker';
import { PollComposer } from './PollComposer';
import { MusicShareComposer } from './MusicShareComposer';
import { AssignmentComposer } from './AssignmentComposer';
import { EmojiPicker } from './EmojiPicker';
import { MESSAGE_MAX_CHARS } from '../emoji';
import { Reply, X } from 'lucide-react';

/** The message being replied to (quote preview in the composer). */
export interface ReplyTarget {
  id: string;
  author: string;
  snippet: string;
}

const DANA_PERSON: Person = { userId: DANA_BOT_USER_ID, name: 'Dana', position: 'AI assistant' };

// @recognition is an ACTION, not a person, and deliberately does not look like
// one in the list. Picking it strips the typed "@…" and flips a transient
// composer mode — nothing is written into the body or into mention_user_ids,
// so a cancelled recognition leaves an ordinary draft behind.
const RECOGNITION_ACTION_ID = '__action:recognition__';
const RECOGNITION_ACTION: Person = {
  userId: RECOGNITION_ACTION_ID,
  name: 'Recognition',
  position: "Recognise someone's contribution",
};
const matchesRecognition = (q: string) =>
  q === '' || 'recognition'.startsWith(q) || 'recognise'.startsWith(q) || 'recognize'.startsWith(q);
// Trailing "@query" (query = a single word, no spaces) at the end of the draft.
const AT_TOKEN = /(^|\s)@([\p{L}\p{N}'._-]*)$/u;

// Persist an unsent draft per conversation so switching threads / navigating away
// (e.g. into an object and back) never loses it (spec acceptance criterion).
const draftStoreKey = (k?: string) => (k ? `kp-conv-draft:${k}` : '');
const readDraft = (k?: string) => {
  if (!k) return '';
  try {
    return localStorage.getItem(draftStoreKey(k)) ?? '';
  } catch {
    return '';
  }
};
const writeDraft = (k: string | undefined, v: string) => {
  if (!k) return;
  try {
    if (v) localStorage.setItem(draftStoreKey(k), v);
    else localStorage.removeItem(draftStoreKey(k));
  } catch {
    /* storage disabled — drafts just aren't persisted */
  }
};

interface Props {
  directory: Directory;
  onSend: (body: string, mentionUserIds: string[], replyToMessageId?: string) => void;
  /** Reply target set from a message's Reply action; cleared after send / on ✕. */
  replyTo?: ReplyTarget | null;
  onCancelReply?: () => void;
  onSendGif?: (gif: MessageGif) => void;
  onSendPoll?: (poll: NewPoll) => void;
  onSendFile?: (file: File) => void;
  onCreateAssignment?: (input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string }) => Promise<void>;
  canAssign?: boolean;
  selfId?: string;
  /** Needed for @recognition: the conversation the recognition is sourced from,
   *  and the bounded context window the classifier may read. */
  conversationId?: string;
  recentMessages?: RecognitionContextMessage[];
  onTyping?: () => void;
  /** Conversation id — keys the persisted draft. */
  draftKey?: string;
}

export const Composer: React.FC<Props> = ({ directory, onSend, onSendGif, onSendPoll, onSendFile, onCreateAssignment, canAssign, selfId, onTyping, draftKey, replyTo, onCancelReply, conversationId, recentMessages }) => {
  const { t } = useTranslation('conversations');
  const [draft, setDraft] = useState(() => readDraft(draftKey));
  const [mentions, setMentions] = useState<Record<string, string>>({}); // userId -> "@Name" token
  const [activeIdx, setActiveIdx] = useState(0);

  // ── Recognition mode (transient) ──────────────────────────────────────────
  const [recognising, setRecognising] = useState(false);
  // Confetti over the composer for a moment after a recognition posts.
  const [celebrating, setCelebrating] = useState(false);
  const recognitionEnabled = !!conversationId;

  // Who is actually in this conversation. The recipient list comes from HERE,
  // not from the AI's candidates: inference may find nobody (a thread of one
  // message, a classifier that is down), and an empty dropdown reads as the
  // feature being broken. Participants are the honest, always-available answer,
  // and they are also the only people it makes sense to recognise from here.
  const [participants, setParticipants] = useState<EmployeeHit[]>([]);
  useEffect(() => {
    if (!recognising || !conversationId || participants.length > 0) return;
    let stopped = false;
    conversationsApi
      .getMembers(conversationId)
      .then((ms) => {
        if (stopped) return;
        setParticipants(
          ms
            .filter((m) => m.user_id !== selfId && m.user_id !== DANA_BOT_USER_ID)
            .map((m) => ({ id: m.employee_id ?? m.user_id, user_id: m.user_id, name: m.name || directory.nameOf(m.user_id) })),
        );
      })
      .catch(() => undefined);
    return () => { stopped = true; };
  }, [recognising, conversationId, participants.length, selfId, directory]);

  const recognitionContext = useMemo(() => ({
    conversationId: conversationId ?? '',
    replyToMessageId: replyTo?.id,
    explicitRecipientUserIds: Object.keys(mentions).filter((id) => id !== DANA_BOT_USER_ID),
    contextMessages: recentMessages ?? [],
  }), [conversationId, replyTo?.id, mentions, recentMessages]);

  const rec = useRecognitionDraft(recognising, draft, recognitionContext);

  // Create the recognition through the EXISTING endpoint. Nothing about
  // recognition is decided here: performance-service validates the recipient,
  // the tenant, the permission and the value, and owns the event, the
  // notification and the audit entry.
  // Leaving the mode hands the words back as an ordinary draft — the text is
  // never discarded, because the person typed it.
  const exitRecognition = useCallback(() => {
    setRecognising(false);
    rec.reset();
  }, [rec]);

  // Paste the news into the thread once the recognition is saved.
  //
  // This is for the room, not for the recipient: performance-service has
  // already notified them, so the announcement deliberately carries NO
  // mentions — two pings for one act of thanks is one too many. It is sent as
  // an ordinary message, which means it is editable, searchable and part of
  // the transcript like anything else said here.
  const announceRecognition = useCallback((created?: PostedRecognition) => {
    setDraft('');
    setMentions({});
    // A save that told us nothing (an older build, a shape we did not expect)
    // still closed the dialog successfully — stay quiet rather than post a
    // half-empty announcement naming nobody.
    if (!created?.recipientName) return;
    onSend(celebrationMessage(created), [], replyTo?.id);
    onCancelReply?.();
    setCelebrating(true);
  }, [onSend, replyTo?.id, onCancelReply]);
  const [menu, setMenu] = useState<null | 'plus' | 'assist' | 'gif' | 'poll' | 'assignment' | 'music' | 'emoji'>(null); // composer "+" actions
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Grow with the text (up to ~40% of the viewport), then scroll inside — so a
  // pasted page is visible and editable instead of a one-line slit.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = Math.max(160, Math.floor(window.innerHeight * 0.4));
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, [draft]);
  useEffect(() => { if (replyTo) taRef.current?.focus(); }, [replyTo]);
  const tooLong = draft.length > MESSAGE_MAX_CHARS;
  const showCounter = draft.length > MESSAGE_MAX_CHARS * 0.75;

  // Insert text at the caret (emoji picker) and keep focus.
  const insertAtCaret = (text: string) => {
    const el = taRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    setDraft((d) => d.slice(0, start) + text + d.slice(end));
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = start + text.length;
      }
    });
  };

  // Pasted images (screenshots, copied pictures) become attachments straight away.
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onSendFile) return;
    const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    e.preventDefault();
    for (const f of files) {
      const named = f.name && f.name !== 'image.png' ? f : new File([f], `pasted-${Date.now()}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type });
      onSendFile(named);
    }
  };

  // Switch drafts when the active conversation changes.
  useEffect(() => {
    setDraft(readDraft(draftKey));
    setMentions({});
  }, [draftKey]);

  // Persist the unsent draft (removed when empty / on send).
  useEffect(() => {
    writeDraft(draftKey, draft);
  }, [draftKey, draft]);

  const query = useMemo(() => {
    const m = draft.match(AT_TOKEN);
    return m ? m[2] : null; // null when not mentioning; '' right after typing '@'
  }, [draft]);

  const suggestions = useMemo<Person[]>(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    const people = directory.search(query);
    const withDana = q === '' || 'dana'.startsWith(q) ? [DANA_PERSON, ...people] : people;
    // Recognition sits at the END of the list: it is an action, and a colleague
    // you were halfway through typing should never be displaced by it.
    const withAction = recognitionEnabled && matchesRecognition(q) ? [...withDana, RECOGNITION_ACTION] : withDana;
    return withAction.slice(0, 6);
  }, [query, directory]);

  // Colleagues who match but have no KaziPeople login. A mention can't reach them, so they
  // are listed greyed out with the reason — typing "@Ally" used to show nothing at all,
  // which read as "mentions are broken" rather than "that person has no account".
  const unmentionable = useMemo(() => {
    if (query === null || query === '') return [];
    return directory.searchUnlinked(query).slice(0, 3);
  }, [query, directory]);

  const pick = (p: Person) => {
    if (p.userId === RECOGNITION_ACTION_ID) {
      // Strip the "@…" the user typed: the trigger is not part of the message,
      // and leaving it would persist a token that means nothing to anyone else.
      setDraft((d) => d.replace(AT_TOKEN, (_m, pre: string) => pre));
      setRecognising(true);
      setActiveIdx(0);
      taRef.current?.focus();
      return;
    }
    const token = `@${p.name}`;
    setDraft((d) => d.replace(AT_TOKEN, (_m, pre: string) => `${pre}${token} `));
    setMentions((prev) => ({ ...prev, [p.userId]: token }));
    setActiveIdx(0);
    taRef.current?.focus();
  };

  // Dictation → editable text: append the transcript to the current draft
  // (never overwrite), keep focus so the user can review/edit, then send
  // manually. Voice input never auto-sends.
  const insertTranscript = (text: string) => {
    setDraft((d) => (d ? `${d.replace(/\s+$/, '')} ${text}` : text));
    onTyping?.();
    taRef.current?.focus();
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    const ids = Object.entries(mentions)
      .filter(([, token]) => text.includes(token))
      .map(([id]) => id);
    // Forgiving @Dana: a literal "@dana" typed in the text counts as mentioning Dana
    // even without picking it from the dropdown (Dana is a known handle). This is what
    // makes "@dana submit leave" trigger Dana without a selection step.
    if (/(^|\s)@dana\b/i.test(text) && !ids.includes(DANA_BOT_USER_ID)) {
      ids.push(DANA_BOT_USER_ID);
    }
    if (text.length > MESSAGE_MAX_CHARS) return; // the counter explains; the server would refuse too
    onSend(text, ids, replyTo?.id);
    setDraft('');
    setMentions({});
    onCancelReply?.();
  };

  const open = suggestions.length > 0 || unmentionable.length > 0;

  return (
    <div className="relative">
      {recognising && rec.phase === 'blocked' && (
        <div className="mb-2 rounded-xl border border-border bg-card p-3" data-testid="recognition-blocked">
          <p className="text-sm font-semibold text-charcoal">
            {t('recognition.title', { defaultValue: 'Give recognition' })}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {rec.blockedReason ||
              t('recognition.blocked', { defaultValue: 'Recognition is not available from conversations in this workspace.' })}
          </p>
          <button type="button" onClick={exitRecognition} className="mt-2 text-xs text-muted-foreground underline">
            {t('common.close', { defaultValue: 'Close' })}
          </button>
        </div>
      )}
      {recognising && rec.phase !== 'blocked' && (
        // The SAME dialog the Recognition page opens — one composer in the
        // product, so a recognition given here is identical to one given
        // there, lands in the same feed and carries the same rules.
        <GiveRecognitionComposer
          initial={{
            recipient: participants.find((p) => p.user_id === rec.suggestion?.recipient?.user_id),
            category: rec.suggestion?.suggested_category_key,
            title: rec.suggestion?.suggested_title,
            message: rec.suggestion?.suggested_message || draft.trim(),
          }}
          // Only people in THIS conversation may be recognised; tagging stays
          // directory-wide, which is the point of tagging a manager.
          searchRecipients={async (q) => {
            const needle = q.trim().toLowerCase();
            return participants.filter((p) => needle === '' || p.name.toLowerCase().includes(needle));
          }}
          source={{
            domain: 'conversation',
            entity_type: 'conversation',
            entity_id: conversationId,
            comment_id: replyTo?.id,
            ai_suggested: !!rec.suggestion && !rec.suggestion.degraded,
          }}
          compact
          onClose={exitRecognition}
          onPosted={(created) => { announceRecognition(created); exitRecognition(); }}
        />
      )}
      {celebrating && <CelebrationBurst onDone={() => setCelebrating(false)} />}
      {open && (
        <ul className="absolute bottom-full left-0 z-10 mb-1 w-64 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          {suggestions.map((p, i) => (
            <li key={p.userId}>
              <button
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => pick(p)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                  i === activeIdx ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-muted/50'
                }`}
              >
                <span
                  className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-semibold ${
                    p.userId === RECOGNITION_ACTION_ID
                      ? 'bg-amber-100 text-amber-700'
                      : p.userId === DANA_BOT_USER_ID
                        ? 'bg-primary-500 text-white'
                        : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {/* An action, not a person: a trophy rather than initials, so
                      the row never reads as another colleague to mention. */}
                  {p.userId === RECOGNITION_ACTION_ID ? (
                    <Trophy size={13} aria-hidden="true" />
                  ) : p.userId === DANA_BOT_USER_ID ? '✦' : p.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-charcoal">{p.name}</span>
                  {p.position && <span className="block truncate text-xs text-muted-foreground">{p.position}</span>}
                </span>
              </button>
            </li>
          ))}
          {unmentionable.map((p) => (
            <li key={`unlinked-${p.name}`} className="border-t border-border first:border-t-0">
              <div className="flex w-full items-center gap-2 px-3 py-1.5 opacity-60" title={t('mention_no_account', { defaultValue: 'No KaziPeople account yet — they can’t receive mentions.' }) as string}>
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                  {p.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-muted-foreground">{p.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {t('mention_no_account_short', { defaultValue: 'No account — can’t be mentioned' })}
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {menu === 'plus' && (
        <PlusMenu
          onDana={() => setMenu('assist')}
          onGif={onSendGif ? () => setMenu('gif') : undefined}
          onPoll={onSendPoll ? () => setMenu('poll') : undefined}
          onFile={onSendFile ? () => { setMenu(null); fileRef.current?.click(); } : undefined}
          onAssignment={onCreateAssignment ? () => setMenu('assignment') : undefined}
          onMusic={() => setMenu('music')}
          onEmoji={() => setMenu('emoji')}
          onClose={() => setMenu(null)}
        />
      )}
      {menu === 'emoji' && (
        <EmojiPicker onPick={(e) => insertAtCaret(e)} onClose={() => setMenu(null)} />
      )}
      {replyTo && (
        <div className="mb-1.5 flex items-start gap-2 rounded-lg border-l-2 border-primary-500 bg-muted/60 px-3 py-1.5 text-xs">
          <Reply className="mt-0.5 h-3.5 w-3.5 flex-none text-primary-500" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-charcoal">{t('reply.replying_to', { defaultValue: 'Replying to {{name}}', name: replyTo.author })}</div>
            <div className="truncate text-muted-foreground">{replyTo.snippet}</div>
          </div>
          <button type="button" onClick={onCancelReply} className="text-muted-foreground hover:text-charcoal" aria-label={t('reply.cancel', { defaultValue: 'Cancel reply' })}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {menu === 'music' && (
        <MusicShareComposer conversationId={draftKey || ''} onClose={() => setMenu(null)} />
      )}
      {menu === 'assignment' && onCreateAssignment && (
        <AssignmentComposer
          directory={directory}
          selfId={selfId}
          canAssign={!!canAssign}
          onCreate={onCreateAssignment}
          onClose={() => setMenu(null)}
        />
      )}
      {onSendFile && (
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onSendFile(f);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
      )}
      {menu === 'assist' && (
        <DanaAssist
          draft={draft}
          onUse={(text) => {
            setDraft(text);
            taRef.current?.focus();
          }}
          onClose={() => setMenu(null)}
        />
      )}
      {menu === 'gif' && onSendGif && (
        <GifPicker onPick={(gif) => onSendGif(gif)} onClose={() => setMenu(null)} />
      )}
      {menu === 'poll' && onSendPoll && (
        <PollComposer onCreate={(poll) => onSendPoll(poll)} onClose={() => setMenu(null)} />
      )}

      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => setMenu((m) => (m ? null : 'plus'))}
          aria-label="Add"
          className="flex-none rounded-xl border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-charcoal"
        >
          <Plus className="h-5 w-5" />
        </button>
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setActiveIdx(0);
            if (e.target.value) onTyping?.();
          }}
          onKeyDown={(e) => {
            // Escape leaves recognition mode before it does anything else, and
            // KEEPS the draft — the words are the person's, not the mode's.
            if (e.key === 'Escape' && recognising) {
              e.preventDefault();
              exitRecognition();
              return;
            }
            if (open) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIdx((i) => Math.min(i + 1, Math.max(0, suggestions.length - 1)));
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIdx((i) => Math.max(i - 1, 0));
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                const choice = suggestions[activeIdx];
                if (!choice) return; // only unmentionable rows are showing
                e.preventDefault();
                pick(choice);
                return;
              }
              if (e.key === 'Escape') {
                setDraft((d) => d.replace(AT_TOKEN, (_m, pre: string) => pre));
                return;
              }
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          onPaste={onPaste}
          rows={1}
          placeholder={t('composer_placeholder')}
          aria-invalid={tooLong || undefined}
          className={`flex-1 resize-none rounded-xl border bg-card px-3 py-2 text-sm text-charcoal placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${tooLong ? 'border-red-400 focus:ring-red-200' : 'border-border focus:border-primary-400 focus:ring-primary-200'}`}
        />
        <div className="flex-none self-center">
          <VoiceInputButton onTranscript={insertTranscript} />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!draft.trim() || tooLong}
          title={tooLong ? t('composer.too_long', { defaultValue: 'Message is too long', max: MESSAGE_MAX_CHARS }) : undefined}
          className="flex-none rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-40"
        >
          {t('composer.send', { defaultValue: 'Send' })}
        </button>
      </div>
      {showCounter && (
        <div className={`mt-1 text-right text-[11px] ${tooLong ? 'text-red-600' : 'text-muted-foreground'}`} aria-live="polite">
          {tooLong
            ? t('composer.too_long', { defaultValue: 'Message is too long — {{count}} / {{max}} characters. Shorten it or send it as a file.', count: draft.length, max: MESSAGE_MAX_CHARS })
            : t('composer.chars', { defaultValue: '{{count}} / {{max}} characters', count: draft.length, max: MESSAGE_MAX_CHARS })}
        </div>
      )}
    </div>
  );
};
